import {
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {UseDocumentPreviewReturnType} from 'platform/document-preview';
import {Box, VStack} from 'platform/foundation';

import {useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  useBulkPostCatalogNumberSuggestionsMutation,
  useGetHandlingUnitsQuery,
  useLazyGetDeliveryNoteDuplicatesQuery,
  usePostDeliveryNoteMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {handleApiError, useVatRatesOptions} from '@dms/shared';

import {
  ExtractDeliveryNoteDocumentMessageInput,
  ExtractDeliveryNoteDocumentResult,
} from '@prompt-bridge/shared/validations';

import {composePath, isNotNilOrEmpty, useBoolean, useNavigate} from 'shared/web';

import {
  DeliveryNoteDocumentPreviewFormDefinition,
  DeliveryNoteDocumentPreviewFormDefinitionSchema,
} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {checkForInvalidItems} from '../utils/checkForInvalidItems';
import {mapDocumentResultToDefaultValues} from '../utils/mapDocumentResultToDefaultValues';
import {mapFormValuesToDeliveryNoteRequest} from '../utils/mapFormValuesToDeliveryNoteRequest';
import {BasicInformationCard} from './BasicInformationCard';
import {Footer} from './Footer';
import {ListOfItemsWrapper} from './ListOfItemsWrapper';

interface DeliveryNoteDocumentPreviewFormProps {
  documentPreview: UseDocumentPreviewReturnType;
  documentInput: ExtractDeliveryNoteDocumentMessageInput | null;
  documentResult: ExtractDeliveryNoteDocumentResult | null;
  onClose: VoidFunction;
}

export function DeliveryNoteDocumentPreviewForm(props: DeliveryNoteDocumentPreviewFormProps) {
  const navigate = useNavigate();

  const [isConfirmed, confirmHeader, unconfirmHeader] = useBoolean(false);

  const formApiRef = useRef<UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>>(null);

  const handlingUnitsQuery = useGetHandlingUnitsQuery();

  const [getDeliveryNoteDuplicates] = useLazyGetDeliveryNoteDuplicatesQuery();
  const [getCatalogNumberSuggestions, initialSuggestionsStatus] =
    useBulkPostCatalogNumberSuggestionsMutation();
  const [postDeliveryNote] = usePostDeliveryNoteMutation();

  const [vatRates] = useVatRatesOptions();

  const defaultHandlingUnitId =
    handlingUnitsQuery.data?.find((handlingUnit) => handlingUnit.isDefault)?.id ?? null;

  const defaultVatRateType = vatRates.find((vat) => vat.type === 'S')?.type ?? 'S';

  const defaultValues = mapDocumentResultToDefaultValues(
    props.documentResult,
    defaultHandlingUnitId,
    defaultVatRateType
  );

  const calculatedDefaultValuesRef =
    useRef<DeliveryNoteDocumentPreviewFormDefinition>(defaultValues);

  const handleSubmit: FormSubmitHandler<DeliveryNoteDocumentPreviewFormDefinition> = async (
    formValues
  ) => {
    if (!isConfirmed) {
      const supplierId = formValues.basicInformation.supplier;
      const invoiceNumber = formValues.basicInformation.invoiceNumber;
      const deliveryNoteNumber = formValues.basicInformation.deliveryNoteNumber;

      const canCheckDuplicates = supplierId && (invoiceNumber || deliveryNoteNumber);

      if (!canCheckDuplicates) {
        return confirmHeader();
      }

      return await getDeliveryNoteDuplicates({
        supplierId: formValues.basicInformation.supplier,
        invoiceNumber: formValues.basicInformation.invoiceNumber,
        deliveryNoteNumber: formValues.basicInformation.deliveryNoteNumber,
      })
        .unwrap()
        .then((result) => {
          if (isNilOrEmpty(result)) {
            formApiRef.current?.setValue('basicInformation.duplicateDeliveryNote', null);

            getCatalogNumberSuggestions({
              supplierId: formValues.basicInformation.supplier,
              searchForOtherSuppliers: true,
              warehouseId: formValues.basicInformation.warehouse,
              searchInOtherWarehouses: true,
              catalogueNumbers: formValues.listOfItems
                .filter((item) => isNotNilOrEmpty(item.number))
                .map((item) => item.number),
            })
              .unwrap()
              .then((result) => {
                if (isNilOrEmpty(result)) {
                  formApiRef.current?.setValue('catalogNumberSuggestions', null);
                  return;
                }

                const suggestionsWithArticles = result.filter((suggestion) =>
                  isNotNil(suggestion.articles)
                );

                formApiRef.current?.setValue(
                  'catalogNumberSuggestions',
                  suggestionsWithArticles.length > 0 ? suggestionsWithArticles : null
                );
              })
              .catch(handleApiError);

            return confirmHeader();
          }
          formApiRef.current?.setValue('basicInformation.duplicateDeliveryNote', result);
        })
        .catch(handleApiError);
    }

    const hasInvalidFields = checkForInvalidItems(formValues.listOfItems);

    if (hasInvalidFields) {
      showNotification.warning(i18n.t('errors.invalid_submit'));
      return;
    }

    const request = mapFormValuesToDeliveryNoteRequest(formValues, vatRates);

    await postDeliveryNote(request)
      .unwrap()
      .then((result) => {
        navigate(
          composePath(warehouseRoutes.deliveryNoteDetailOverview, {
            params: {id: result.deliveryNoteId},
          })
        );
      })
      .catch(() => {
        showNotification.error(
          i18n.t('entity.documentExtraction.notifications.createDeliveryNoteIssue')
        );
      });
  };

  const handleSaveCalculatedPrices = (values: DeliveryNoteDocumentPreviewFormDefinition) => {
    const updatedDefaultValues = {
      ...defaultValues,
      listOfItems: defaultValues.listOfItems.map((item, index) => ({
        ...item,
        totalPriceWithVat: values.listOfItems[index]?.totalPriceWithVat ?? 0,
      })),
    };
    calculatedDefaultValuesRef.current = updatedDefaultValues;
  };

  const handleDiscard = () => {
    formApiRef.current?.reset(calculatedDefaultValuesRef.current);
    unconfirmHeader();
  };

  return (
    <Box flex={1} paddingLeft={4} height="100%" minWidth="60%">
      <DataStatus isLoading={handlingUnitsQuery.isLoading} minHeight="100%">
        <Form
          isFullHeight
          experimentalZodSchema={DeliveryNoteDocumentPreviewFormDefinitionSchema(isConfirmed)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
        >
          {(control, formApi) => {
            formApiRef.current = formApi;

            return (
              <>
                <VStack spacing={4} height="100%">
                  <FormField type="hidden" control={control} name="catalogNumberSuggestions" />
                  <BasicInformationCard
                    isConfirmed={isConfirmed}
                    control={control}
                    formApi={formApi}
                    documentPreview={props.documentPreview}
                    onEdit={unconfirmHeader}
                  />
                  <ListOfItemsWrapper
                    isConfirmed={isConfirmed}
                    isLoadingSuggestions={initialSuggestionsStatus.isLoading}
                    control={control}
                    formApi={formApi}
                    documentPreview={props.documentPreview}
                    onPricesCalculated={handleSaveCalculatedPrices}
                  />
                </VStack>
                <Footer
                  isConfirmed={isConfirmed}
                  control={control}
                  formApi={formApi}
                  onDiscard={handleDiscard}
                  onClose={props.onClose}
                />
              </>
            );
          }}
        </Form>
      </DataStatus>
    </Box>
  );
}
